import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { ID_GROUPS, RARITY, ROLES, ROLE_MAP, UNIT_GROUPS } from '../config';
import { usePerservedState } from '../utils/versionDataUtil';

import { ReactComponent as SortIcon } from '../assets/svgs/sort.svg';
import { ReactComponent as RefreshIcon } from '../assets/svgs/refresh.svg';
import ScrollArrow from '../components/ScrollArrow';

import redElement from '../assets/images/colours/red_w.webp';
import greenElement from '../assets/images/colours/green_w.webp';
import yellowElement from '../assets/images/colours/yellow_w.webp';
import blueElement from '../assets/images/colours/blue_w.webp';

import GroupGrid from '../components/GroupGrid';
import SearchBar from '../components/SearchBar';
import './pages.css';

const colours = [
    { code: 'r', className: 'element-red', image: redElement, alt: 'Red' },
    { code: 'g', className: 'element-green', image: greenElement, alt: 'Green' },
    { code: 'y', className: 'element-yellow', image: yellowElement, alt: 'Yellow' },
    { code: 'b', className: 'element-blue', image: blueElement, alt: 'Blue' },
];

const roles = [
    { code: 't', image: ROLES.tank, alt: 'Tank' },
    { code: 'a', image: ROLES.dps, alt: 'Offense' },
    { code: 'h', image: ROLES.heal, alt: 'Support' },
];

const rarities = [
    { code: 'n', image: RARITY.rarity_n, alt: 'Standard' },
    { code: 'l', image: RARITY.rarity_l, alt: 'Limited' },
    { code: 's', image: RARITY.rarity_s, alt: 'SF' },
];


function CharactersPage({ characterData }) {
    const intl = useIntl();
    const navigate = useNavigate();
    const groupContainerRef = useRef(null);
    const [filteredCharacters, setFilteredCharacters] = useState({});
    const defaultFilterState = {
        searchText: '',
        colours: ['r', 'g', 'y', 'b'],
        roles: ['t', 'a', 'h'],
        rarity: ['n', 'l', 's'],
        sortBy: 'default',
        sortDirection: 'asc',
        groups: []
    };

    const [filterState, setFilterState] = usePerservedState('charactersPageState', defaultFilterState)

    const sortOptions = [
        { value: 'default', label: intl.formatMessage({ id: 'sort.default', defaultMessage: 'Default' }) },
        { value: 'id', label: intl.formatMessage({ id: 'sort.character', defaultMessage: 'Character' }) },
        { value: 'role', label: intl.formatMessage({ id: 'sort.role', defaultMessage: 'Role' }) },
        { value: 'position', label: intl.formatMessage({ id: 'sort.position', defaultMessage: 'Position' }) },
    ];

    useEffect(() => {
        if (characterData && Object.keys(characterData).length > 0) {
            let filteredAndSorted = applyFilters(characterData)

            setFilteredCharacters(filteredAndSorted);
        } else {
            setFilteredCharacters([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [characterData, filterState]);

    const resetFilters = () => {
        setFilterState(defaultFilterState);
    }

    const handleCharacterClick = (characterKey) => {
        navigate(`/character/${characterKey}`);
    };

    const handleFilterClick = (filterType, value) => {
        setFilterState(prevState => {
            const currentValues = prevState[filterType];
            const newValues = currentValues.includes(value)
                ? currentValues.filter(v => v !== value)
                : [...currentValues, value];
            return { ...prevState, [filterType]: newValues };
        });
    };

    const handleSearchChange = (value) => {
        const newSearchText = value;
        setFilterState(prevState => {
            if (prevState.searchText !== newSearchText) {
                return { ...prevState, searchText: newSearchText };
            }
            return prevState;
        });
    };

    const handleSortChange = (event) => {
        const selectedValue = event.target.value;
        const [sortBy, sortDir] = selectedValue.split('-');

        setFilterState(prevState => ({
            ...prevState,
            sortBy: sortBy,
            sortDirection: sortDir || (prevState.sortBy === sortBy ? (prevState.sortDirection === 'asc' ? 'desc' : 'asc') : 'asc'),
        }));
    };

    const getSortValue = (field) => {
        return `${field}-${filterState.sortBy === field ? filterState.sortDirection : ''}`
    }

    const applyFilters = (data) => {
        if (!data) return [];

        const { searchText, colours, roles, rarity, groups, sortBy, sortDirection } = filterState;

        const lowerCaseSearchText = searchText.toLowerCase();

        let filtered = Object.values(data);

        filtered = filtered.filter(character => {
            const nameMatch = (character.name ?? '').toLowerCase().includes(lowerCaseSearchText);
            const titleMatch = (character.title ?? '').toLowerCase().includes(lowerCaseSearchText);
            const colourMatch = colours.includes((character.colour ?? '').toLowerCase());
            const rolesMatch = roles.includes((character.role ?? '').toLowerCase());
            const rarityMatch = rarity.includes((character.rarity ?? '').toLowerCase());

            if (groups.length > 0) {
                const characterGroups = character.groups ?? [];
                const hasOtherGroup = characterGroups.some(charGroup => !UNIT_GROUPS.includes(charGroup));

                return (nameMatch || titleMatch) && colourMatch && rolesMatch && rarityMatch && groups.every(filterGroup => {
                    return filterGroup === "Others" ? hasOtherGroup : characterGroups.includes(filterGroup);
                });
            } else {
                return (nameMatch || titleMatch) && colourMatch && rolesMatch && rarityMatch;
            }
        });

        if (sortBy) {
            filtered.sort((a, b) => {
                const aValue = (a[sortBy] ?? '').toString().toLowerCase();
                const bValue = (b[sortBy] ?? '').toString().toLowerCase();

                switch (sortBy) {
                    case "position":
                        return (sortDirection === 'asc' ? a[sortBy] - b[sortBy] : b[sortBy] - a[sortBy]);
                    case "role":
                        const roleOrder = { t: 0, a: 1, h: 2 };
                        return (sortDirection === 'asc' ? roleOrder[aValue] - roleOrder[bValue] : roleOrder[bValue] - roleOrder[aValue]);
                    case "default":
                    case "id":
                        return sortDirection === 'asc' ? a.id - b.id : b.id - a.id;
                    default:
                        return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
                }
            });

            let restructured = {};

            switch (sortBy) {
                case "role":
                    restructured = filtered.reduce((acc, character) => {
                        const role = character.role?.toLowerCase();
                        acc[role] = acc[role] || [];
                        acc[role].push(character);
                        return acc;
                    }, {});
                    break;
                case "id":
                    restructured = filtered.reduce((acc, character) => {
                        const shortID = character.id.slice(0, 4);
                        const groupID = ID_GROUPS[shortID];
                        acc[groupID] = acc[groupID] || [];
                        acc[groupID].push(character);
                        return acc;
                    }, {});
                    break;
                default:
                    restructured = { d: filtered };
            }

            filtered = restructured;
        }

        return filtered;
    };

    if (Object.keys(characterData).length === 0) {
        return (
            <div className="page">
                <div className="main-container">
                    <div className="surround-margin"></div>
                    <div className="sub-container">
                        <h1 className="main-header">
                            <FormattedMessage
                                id="main.characters"
                                defaultMessage="Characters"
                            />
                            <RefreshIcon className="refresh-icon" />
                        </h1>
                        <div className="main-filter-container">
                            <input
                                type="search"
                                placeholder={intl.formatMessage({ id: 'main.search', defaultMessage: 'Search' })}
                                className="search-bar"
                                value=""
                                readOnly
                            />
                            <div className="filter-container">
                                {colours.map(colour => (
                                    <div
                                        key={colour.code}
                                        className={`filter-colour ${colour.className} ${filterState.colours.includes(colour.code) ? 'active' : ''}`}
                                    >
                                        <img className={colour.className === 'element-green' ? 'element-green-image' : 'element-image'} src={colour.image} alt={colour.alt} />
                                    </div>
                                ))}
                            </div>
                            <div className="filter-container">
                                {roles.map(role => (
                                    <div
                                        key={role.code}
                                        className={`role-filter ${filterState.roles.includes(role.code) ? 'active' : ''}`}
                                    >
                                        <img className="role-image" src={role.image} alt={role.alt} />
                                    </div>
                                ))}
                            </div>
                            <div className="filter-container">
                                {rarities.map(rarity => (
                                    <div
                                        key={rarity.code}
                                        className={`role-filter ${filterState.rarity.includes(rarity.code) ? 'active' : ''}`}
                                    >
                                        <img className="ticket-image" src={rarity.image} alt={rarity.alt} />
                                    </div>
                                ))}
                            </div>
                            <div className="group-container">
                                <div className="buff-title-container">
                                    <div>
                                        <FormattedMessage
                                            id="sort.groups"
                                            defaultMessage="Groups"
                                        />
                                    </div>
                                    <div className="filter-bubble">
                                        All
                                    </div>
                                </div>
                            </div>
                            <div className="sort-container">
                                <div className="sort-select-container">
                                    <select className="sort-select" value="Default" readOnly>
                                        <option>
                                            Default
                                        </option>
                                    </select>
                                </div>
                                <SortIcon
                                    className="sort-icon asc"
                                />
                            </div>
                        </div>
                        <div>
                            <div className="character-grid">
                                {Array.from({ length: 18 }).map((_, index) => (
                                    <div
                                        className="character-container"
                                        key={index}
                                    >
                                        <div className="image-container">
                                            <div className="loading-image" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <br />
                        </div>
                    </div>
                    <div className="surround-margin"></div>
                </div>
            </div >
        );
    }

    return (
        <div className="page">
            <div className="main-container">
                <div className="surround-margin"></div>
                <div className="sub-container">
                    <h1 className="main-header">
                        <FormattedMessage
                            id="main.characters"
                            defaultMessage="Characters"
                        />
                        <RefreshIcon onClick={() => resetFilters()} className="refresh-icon" />
                    </h1>
                    <div className="main-filter-container">
                        <SearchBar filterState={filterState} handleSearchChange={handleSearchChange} />
                        <div className="filter-container">
                            {colours.map(colour => (
                                <div
                                    key={colour.code}
                                    className={`filter-colour ${colour.className} ${filterState.colours.includes(colour.code) ? 'active' : ''}`}
                                    onClick={() => handleFilterClick('colours', colour.code)}
                                >
                                    <img className={colour.className === 'element-green' ? 'element-green-image' : 'element-image'} src={colour.image} alt={colour.alt} />
                                </div>
                            ))}
                        </div>
                        <div className="filter-container">
                            {roles.map(role => (
                                <div
                                    key={role.code}
                                    className={`role-filter ${filterState.roles.includes(role.code) ? 'active' : ''}`}
                                    onClick={() => handleFilterClick('roles', role.code)}
                                >
                                    <img className="role-image" src={role.image} alt={role.alt} />
                                </div>
                            ))}
                        </div>
                        <div className="filter-container">
                            {rarities.map(rarity => (
                                <div
                                    key={rarity.code}
                                    title={rarity.alt}
                                    className={`role-filter ${filterState.rarity.includes(rarity.code) ? 'active' : ''}`}
                                    onClick={() => handleFilterClick('rarity', rarity.code)}
                                >
                                    <img className="ticket-image" src={rarity.image} alt={rarity.alt} />
                                </div>
                            ))}
                        </div>
                        <GroupGrid filterState={filterState} handleFilterClick={handleFilterClick} groupContainerRef={groupContainerRef} />
                        <div className="sort-container">
                            <div className="sort-select-container">
                                <select className="sort-select" value={getSortValue(filterState.sortBy)} onChange={handleSortChange}>
                                    {sortOptions.map((option) => (
                                        <option key={option.value} value={getSortValue(option.value)}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <SortIcon
                                className={`sort-icon ${filterState.sortDirection}`}
                                onClick={() => setFilterState(prev => ({ ...prev, sortDirection: prev.sortDirection === 'asc' ? 'desc' : 'asc' }))}
                            />
                        </div>
                    </div>
                    {Object.entries(filteredCharacters).map(([key, grid]) => (
                        <div key={key}>
                            <div className="character-grid">
                                {Object.values(grid).map((character, characterIndex) => (
                                    <div
                                        className="character-container"
                                        onClick={() => handleCharacterClick(character.id)}
                                        key={`${key}-${characterIndex}`}
                                    >
                                        <div className="image-container">
                                            <div className="image-zoom">
                                                <img
                                                    src={character.images?.m}
                                                    alt={character.name}
                                                    className="character-image medium"
                                                />
                                            </div>
                                            <div className="character-name">
                                                <div>{character.name}:</div>
                                                <div>{character.title}</div>
                                            </div>
                                            <img
                                                src={ROLES[`${ROLE_MAP[character.role]}_${character.colour}`]}
                                                className="character-role"
                                                alt={`${characterIndex}-role`}
                                            />
                                            <div className="character-gear-container">
                                                <img
                                                    loading="lazy"
                                                    src={character.gear1.image}
                                                    className="character-gear"
                                                    alt={`${characterIndex}-gear`}
                                                />
                                                {character.gear2 && (
                                                    <img
                                                        loading="lazy"
                                                        src={character.gear2.image}
                                                        className="character-gear"
                                                        alt={`${characterIndex}-gear2`}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <br />
                        </div>
                    ))}
                    <br />
                    <div className="bottom-controls full">
                        <div></div>
                        {Object.values(filteredCharacters).reduce((sum, grid) => sum + Object.keys(grid).length, 0) > 25 && (
                            <div className="bottom-contorls-right">
                                <ScrollArrow />
                            </div>
                        )}
                    </div>
                </div>
                <div className="surround-margin"></div>
            </div>
        </div >
    );
}

export default CharactersPage;